import { useState } from "react";
import { useAtomValue } from "jotai";
import { useOutletContext } from "react-router-dom";
import { apiCall } from "../../lib/api-client.js";
import {
  ApiError,
  type ProjectAdminsResponse,
  type PublicUser,
  type UsersListResponse,
} from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import { useApiQuery } from "../../lib/useApiQuery.js";
import { projectAdminsPath, usersPath } from "../../lib/api-paths.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";
import { Empty } from "../../components/Empty.js";
import { Modal } from "../../components/Modal.js";
import {
  FormField,
  PrimaryButton,
  SecondaryButton,
  TextInput,
} from "../../components/FormField.js";
import type { OrgDetailContext } from "./context.js";

/**
 * N-76 R6.1/R6.2 — 项目管理员名单页签。仿 LeadershipPanel，但**按用户名操作**（不是
 * user._id）：企业端按用户名比对。LeadershipPanel 的 leaderUserIds 存 Mongo _id，下发前
 * 由后端 `buildLeadershipMap` 统一换成用户名（N-98 修掉了「下发 _id」的坑）；本页签从
 * 选人到保存全程只传 `PublicUser.user`，不摸 `PublicUser.id`。
 */
export function ProjectAdminsPanel(): JSX.Element {
  const { org } = useOutletContext<OrgDetailContext>();
  const auth = useAtomValue(authAtom);
  const [picking, setPicking] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const adminsQ = useApiQuery<ProjectAdminsResponse>(
    projectAdminsPath(org.code),
    [org.code],
  );
  const users = adminsQ.data?.users ?? [];

  const save = async (nextUsers: string[]): Promise<void> => {
    if (!auth) return;
    try {
      await apiCall("PUT", projectAdminsPath(org.code), {
        body: { users: nextUsers },
        token: auth.token,
      });
      setActionError(null);
      setPicking(false);
      await adminsQ.refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "保存失败");
    }
  };

  const handleRemove = (user: string): Promise<void> =>
    save(users.filter((u) => u !== user));

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-dosia-text-secondary">
        名单里的人能看到全部项目的共享文件夹；其他人只能看到自己在项目档案里被 @ 的项目。
      </p>
      {adminsQ.error ? <ErrorBanner>{adminsQ.error.message}</ErrorBanner> : null}
      {actionError ? (
        <ErrorBanner onDismiss={() => setActionError(null)}>
          {actionError}
        </ErrorBanner>
      ) : null}

      <div className="flex items-center justify-between">
        <span className="text-sm text-dosia-text-secondary">
          共 {users.length} 名项目管理员
        </span>
        <PrimaryButton onClick={() => setPicking(true)}>
          管理名单
        </PrimaryButton>
      </div>

      {adminsQ.loading ? (
        <p className="text-sm text-dosia-text-dim">加载中...</p>
      ) : users.length === 0 ? (
        <Empty title="尚未设置项目管理员" />
      ) : (
        <ul className="flex flex-wrap gap-2">
          {users.map((u) => (
            <li
              key={u}
              className="inline-flex items-center gap-1.5 rounded-full bg-dosia-accent/15 text-dosia-text px-3 py-1 text-sm font-mono"
            >
              {u}
              <button
                type="button"
                aria-label={`移除 ${u}`}
                className="text-dosia-text-dim hover:text-amber-700"
                onClick={() => void handleRemove(u)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {picking ? (
        <ProjectAdminPickerDialog
          orgCode={org.code}
          selected={users}
          onCancel={() => setPicking(false)}
          onSubmit={save}
        />
      ) : null}
    </div>
  );
}

function ProjectAdminPickerDialog({
  orgCode,
  selected: initialSelected,
  onCancel,
  onSubmit,
}: {
  orgCode: string;
  selected: string[];
  onCancel: () => void;
  onSubmit: (users: string[]) => Promise<void>;
}): JSX.Element {
  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [q, setQ] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const qs = new URLSearchParams({ pageSize: "200", status: "active" });
  if (q.trim()) qs.set("q", q.trim());
  const { data, loading } = useApiQuery<UsersListResponse>(
    `${usersPath(orgCode)}?${qs.toString()}`,
    [orgCode, q],
  );

  const toggle = (username: string): void => {
    setSelected((curr) =>
      curr.includes(username)
        ? curr.filter((x) => x !== username)
        : [...curr, username],
    );
  };

  return (
    <Modal
      open
      onClose={onCancel}
      title="项目管理员名单"
      size="lg"
      footer={
        <>
          <SecondaryButton onClick={onCancel}>取消</SecondaryButton>
          <PrimaryButton
            disabled={submitting}
            onClick={async () => {
              setSubmitting(true);
              await onSubmit(selected);
              setSubmitting(false);
            }}
          >
            保存（已选 {selected.length}）
          </PrimaryButton>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <FormField label="搜索用户" hint="按 user 名 / 飞书姓名">
          <TextInput value={q} onChange={(e) => setQ(e.target.value)} />
        </FormField>
        {loading ? (
          <p className="text-sm text-dosia-text-dim">加载中...</p>
        ) : (
          <ul className="max-h-[320px] overflow-auto border border-dosia-border rounded-md divide-y divide-dosia-border-light">
            {(data?.rows ?? []).map((u: PublicUser) => (
              <li
                key={u.id}
                className="flex items-center gap-2 px-3 py-2 hover:bg-dosia-bg-lighter"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(u.user)}
                  onChange={() => toggle(u.user)}
                  aria-label={`选择 ${u.user}`}
                />
                <span className="font-mono text-sm">{u.user}</span>
                {u.feishu?.name ? (
                  <span className="text-xs text-dosia-text-secondary">
                    （{u.feishu.name}）
                  </span>
                ) : null}
                {u.role ? (
                  <span className="ml-auto text-xs text-dosia-text-dim">
                    {u.role}
                  </span>
                ) : null}
              </li>
            ))}
            {(data?.rows ?? []).length === 0 ? (
              <li className="px-3 py-4 text-center text-sm text-dosia-text-dim">
                无匹配用户
              </li>
            ) : null}
          </ul>
        )}
      </div>
    </Modal>
  );
}
